import type { AudioFrame } from "@/utils/arcade/beat";
import { Fx } from "./fx";
import { createProgression, LEVEL_SECONDS } from "./progression";
import { drawBeatFlash, drawCoverBackdrop, drawGlow, drawLightRays, drawSpectrumRing, drawVinyl, glowText, withAlpha, type Ctx2D } from "./render";
import { createFeedbackQueue, createTally, EMPTY_INPUT, pressedAction, tapInput, type FrameInput, type GameEngine, type GameEnv, type GameProbe, type GameStatus } from "./types";

type Ring = 0 | 1;
// What put a spike there. Each level adds one: single spikes and the wheel
// from the start, then clusters, swinging spikes, gates, comets and blinkers.
type Pattern = "spike" | "cluster" | "mover" | "gate" | "comet" | "blink" | "wheel";

interface OrbitItem {
  kind: "spike" | "note" | "shield";
  pattern: Pattern;
  ring: Ring;
  // Blinkers start on `ring` and jump to `toRing` at age `hopAt`, well before
  // they reach the player. Everything else has toRing = ring and hopAt -1.
  toRing: Ring;
  hopAt: number;
  // The angle it rests on, plus its own drift and swing.
  center: number;
  vel: number;
  amp: number;
  swingRate: number;
  swingPhase: number;
  age: number;
  // Seconds of warning before a spike can hurt. It fades in over this time.
  arm: number;
  // Seconds left of a harmless fade out, or -1 while it is live.
  dying: number;
  gone: boolean;
  grazed: boolean;
}

export interface VinylOrbitDebug {
  angle: number;
  ring: Ring;
  effectiveRing: Ring;
  direction: number;
  speed: number;
  level: number;
  reach: [number, number];
  items: Array<{ kind: OrbitItem["kind"]; pattern: Pattern; ring: Ring; angle: number; age: number; dangerous: boolean }>;
  byLevel: Record<number, Record<string, number>>;
  wheelsCleared: number;
}

const MAX_LIVES = 3;
const ARM_SECONDS = 0.8;
const WHEEL_ARM_SECONDS = 1.2;
const SWITCH_SECONDS = 0.14;
const FLIP_WARN_SECONDS = 1.4;
const WHEEL_START = 22.5;
const WHEEL_END = 29.7;
const TAU = Math.PI * 2;

// Autopilot plan: 28 steps of 50ms, about a second and a half ahead.
const PLAN_STEP = 0.05;
const PLAN_STEPS = 28;
const PLAN_COOLDOWN = 5;

const angleGap = (a: number, b: number) => {
  const diff = Math.abs(((a - b) % TAU) + TAU) % TAU;
  return Math.min(diff, TAU - diff);
};

// Distance going forward from `from` to `to`, in [0, 2PI).
const ahead = (from: number, to: number) => (((to - from) % TAU) + TAU) % TAU;
const clamp = (value: number, low: number, high: number) => (value < low ? low : value > high ? high : value);

const LEVEL_LABELS = ["Reverse spin and spike clusters", "Swinging spikes and mid-level flips", "Spike gates: weave through", "Comets run the rings", "Blinkers jump rings", "Faster, busier"];

// One button game around a spinning record. The player circles on one of two
// rings and a tap hops rings. Spikes fade in well ahead of the player before
// they can hurt, every level adds a new kind of hazard, and each level ends
// with the spike wheel: spokes that spin against you for a few seconds.
export function createVinylOrbit(env: GameEnv): GameEngine & { inspect(): VinylOrbitDebug } {
  const { theme, rng, art } = env;
  const difficulty = env.difficulty;
  const { palette } = theme;
  const fx = new Fx(rng, env.reducedMotion, art, env.quiet);
  const progression = createProgression(fx, palette, LEVEL_LABELS);
  let width = env.width;
  let height = env.height;

  let time = 0;
  let score = 0;
  let combo = 0;
  let lives = MAX_LIVES;
  let over = false;
  let damage = 0;
  let invulnerable = 0;
  let shield = false;
  let angle = -Math.PI / 2;
  let direction = 1;
  let ring: Ring = 1;
  let switchProgress = 1;
  let recordSpin = 0;
  let beats = 0;
  let items: OrbitItem[] = [];
  let lastLevel = 1;
  let flipIn = -1;
  let flipDoneThisLevel = false;
  let wheelOn = false;
  let wheelSpin = 0;
  let wheelSpeed = 0;
  let wheelHitAt = 0;
  let wheelsCleared = 0;
  let introduced = new Set<Pattern>(["spike", "wheel"]);
  let pendingNew: Pattern | null = null;
  let skipSpikes = 0;
  let lastSpikeRing: Ring = 0;
  let lastNear = -10;
  let speedNow = 1.2;
  let lastTap = -10;
  // Banners land on top of the record, so the centre is dimmed while one shows.
  let bannerShade = 0;
  let wasFever = false;
  const tally = createTally();
  const byLevel: Record<number, Record<string, number>> = {};
  const feedbackQueue = createFeedbackQueue();
  const trail: Array<{ x: number; y: number }> = [];
  // Autopilot scratch, reused every frame.
  const danger = new Float32Array(PLAN_STEPS * 2);
  const value = new Float32Array((PLAN_STEPS + 3) * 2 * (PLAN_COOLDOWN + 1));

  const center = () => ({ x: width / 2, y: height * 0.54 });
  const base = () => Math.min(width, height * 0.92);
  const recordRadius = () => base() * 0.18;
  const ringRadius = (index: Ring) => base() * (index === 0 ? 0.28 : 0.41);
  const playerRadius = () => Math.max(7, base() * 0.022);
  const spikeRadius = () => playerRadius() * 1.05;
  // How close in angle a spike on that ring has to be to hit.
  const reach = (index: Ring) => (playerRadius() + spikeRadius()) / ringRadius(index);
  const levelTime = () => time - (progression.level - 1) * LEVEL_SECONDS;
  const speedFor = () => Math.min(2.6, (1.2 + (progression.level - 1) * 0.18 + clamp(levelTime() / LEVEL_SECONDS, 0, 1) * 0.15) * difficulty);
  const spikeRate = () => (progression.level <= 1 ? 0.5 : Math.min(0.92, 0.55 + (progression.level - 2) * 0.1));
  // Clear time between spikes on opposite rings: always enough to hop.
  const sepSeconds = () => Math.max(0.3, 0.95 - (progression.level - 1) * 0.16);
  const leadSeconds = () => Math.max(1.4, 2.0 - (progression.level - 1) * 0.12);
  // Clean play builds a multiplier on everything: notes and close passes
  // raise it, a hit drops it back to x1.
  const multiplier = () => (1 + Math.min(4, Math.floor(combo / 5)) * 0.5) * progression.multiplier;

  const position = (radius: number, a: number) => {
    const c = center();
    return { x: c.x + Math.cos(a) * radius, y: c.y + Math.sin(a) * radius };
  };

  const currentRadius = () => {
    const from = ringRadius(ring === 0 ? 1 : 0);
    const to = ringRadius(ring);
    const t = Math.min(1, switchProgress);
    return from + (to - from) * t * (2 - t);
  };

  const effectiveRing = (): Ring => (switchProgress >= 0.5 ? ring : ring === 0 ? 1 : 0);
  const angleOf = (item: OrbitItem, t = 0) => item.center + item.vel * t + item.amp * Math.sin(item.swingPhase + item.swingRate * (item.age + t));
  const ringAt = (item: OrbitItem, t = 0): Ring => (item.hopAt >= 0 && item.age + t >= item.hopAt ? item.toRing : item.ring);
  const dangerous = (item: OrbitItem, t = 0) => item.kind === "spike" && item.dying < 0 && item.age + t >= item.arm;

  // When the player meets an item, in seconds from now, as a window padded by
  // the hit reach. Assumes today's speed and direction, which is why a flip
  // clears the board.
  const meeting = (item: { center: number; vel: number; amp: number; toRing: Ring }) => {
    const closing = speedNow - item.vel * direction;
    const d = direction > 0 ? ahead(angle, item.center) : ahead(item.center, angle);
    const pad = reach(item.toRing) + item.amp;
    return { from: (d - pad) / closing, to: (d + pad) / closing };
  };

  // Would a spike on `ring` meeting the player in [from, to] leave no time to
  // hop from a spike on the other ring?
  const conflicts = (ringIndex: Ring, from: number, to: number) => {
    const sep = sepSeconds();
    for (const item of items) {
      if (item.kind !== "spike" || item.dying >= 0 || item.toRing === ringIndex) continue;
      const m = meeting(item);
      if (m.to < -0.2) continue;
      if (from < m.to + sep && to > m.from - sep) return true;
    }
    return false;
  };

  const count = (pattern: string) => {
    tally.add(pattern);
    const level = progression.level;
    const row = (byLevel[level] ??= {});
    row[pattern] = (row[pattern] ?? 0) + 1;
  };

  const makeItem = (kind: OrbitItem["kind"], pattern: Pattern, ringIndex: Ring, at: number, extra: Partial<OrbitItem> = {}): OrbitItem => ({
    kind,
    pattern,
    ring: ringIndex,
    toRing: ringIndex,
    hopAt: -1,
    center: at,
    vel: 0,
    amp: 0,
    swingRate: 0,
    swingPhase: 0,
    age: 0,
    arm: kind === "spike" ? ARM_SECONDS : 0.3,
    dying: -1,
    gone: false,
    grazed: false,
    ...extra,
  });

  // Tries to put spikes down `lead` seconds ahead. Returns false if every
  // placement would trap the player, and nothing is placed.
  const tryPlace = (pattern: Pattern, lead: number, ringIndex: Ring): boolean => {
    const w = speedNow;
    const at = (seconds: number) => angle + direction * w * seconds;
    const fits = (candidate: OrbitItem) => {
      const m = meeting(candidate);
      return !conflicts(candidate.toRing, m.from, m.to);
    };
    const batch: OrbitItem[] = [];
    if (pattern === "spike") batch.push(makeItem("spike", pattern, ringIndex, at(lead)));
    else if (pattern === "cluster") {
      const n = progression.level >= 4 ? 3 : 2;
      for (let i = 0; i < n; i++) batch.push(makeItem("spike", pattern, ringIndex, at(lead) + direction * i * reach(ringIndex) * 1.6));
    } else if (pattern === "mover") {
      batch.push(makeItem("spike", pattern, ringIndex, at(lead), { amp: 0.2, swingRate: 2.4, swingPhase: rng() * TAU }));
    } else if (pattern === "gate") {
      // A weave: spikes that alternate rings, each gap just wide enough.
      const n = progression.level >= 6 ? 4 : 3;
      const step = sepSeconds() + (2 * Math.max(reach(0), reach(1))) / w + 0.08;
      for (let i = 0; i < n; i++) batch.push(makeItem("spike", pattern, ((ringIndex + i) % 2) as Ring, at(lead + i * step)));
    } else if (pattern === "blink") {
      // Starts on the other ring and jumps across 0.8s before it arrives.
      const other: Ring = ringIndex === 0 ? 1 : 0;
      batch.push(makeItem("spike", pattern, other, at(lead), { toRing: ringIndex, hopAt: Math.max(ARM_SECONDS, lead - 0.8) }));
    } else if (pattern === "comet") {
      const speed = w * 0.55;
      const seconds = Math.min(lead + 0.3, 4 / (w + speed));
      batch.push(makeItem("spike", pattern, ringIndex, angle + direction * (w + speed) * seconds, { vel: -direction * speed }));
    }
    for (let i = 0; i < batch.length; i++) {
      if (!fits(batch[i])) return false;
      // Spikes inside a pattern must also leave each other a way through.
      for (let j = 0; j < i; j++) {
        if (batch[j].toRing === batch[i].toRing) continue;
        const a = meeting(batch[j]);
        const b = meeting(batch[i]);
        if (b.from < a.to + sepSeconds() && b.to > a.from - sepSeconds()) return false;
      }
    }
    for (const item of batch) items.push(item);
    lastSpikeRing = batch[batch.length - 1].ring;
    count(pattern);
    if (pattern === "gate") skipSpikes = 2;
    return true;
  };

  const unlocked = (): Pattern[] => {
    const level = progression.level;
    const list: Pattern[] = ["spike"];
    if (level >= 2) list.push("cluster");
    if (level >= 3) list.push("mover");
    if (level >= 4) list.push("gate");
    if (level >= 5) list.push("comet");
    if (level >= 6) list.push("blink");
    return list;
  };

  const placeNote = (kind: "note" | "shield") => {
    const lead = leadSeconds() * (0.7 + rng() * 0.3);
    const ringIndex: Ring = rng() < 0.5 ? 0 : 1;
    items.push(makeItem(kind, "spike", ringIndex, angle + direction * speedNow * lead));
    count(kind);
  };

  const spawnOnBeat = () => {
    beats += 1;
    const lt = levelTime();
    if (flipIn >= 0 || wheelOn || lt > WHEEL_START - 1.5) return;
    if (beats % 22 === 11 && !shield) {
      placeNote("shield");
      return;
    }
    const empty = !items.some((item) => item.kind === "spike" && item.dying < 0);
    const spikeBeat = empty || (progression.level <= 1 ? beats % 2 === 0 : rng() < spikeRate());
    if (!spikeBeat || skipSpikes > 0) {
      skipSpikes = Math.max(0, skipSpikes - 1);
      placeNote("note");
      return;
    }
    let pattern: Pattern;
    if (pendingNew) {
      pattern = pendingNew;
    } else {
      const options = unlocked();
      pattern = options[Math.floor(rng() * options.length)];
    }
    // Later levels lean toward weaving: the next spike goes on the other
    // ring from the last one, so the hops come on the beat.
    const weave = rng() < Math.min(0.8, (progression.level - 1) * 0.2);
    const first: Ring = weave ? (lastSpikeRing === 0 ? 1 : 0) : rng() < 0.5 ? 0 : 1;
    const lead = leadSeconds();
    const placed = tryPlace(pattern, lead, first) || tryPlace(pattern, lead, first === 0 ? 1 : 0) || tryPlace(pattern, lead + 0.3, first) || tryPlace(pattern, lead + 0.3, first === 0 ? 1 : 0);
    if (placed && pattern === pendingNew) pendingNew = null;
    if (!placed) placeNote("note");
  };

  const clearBoard = () => {
    for (const item of items) if (item.dying < 0) item.dying = 0.35;
  };

  const flip = () => {
    direction = -direction;
    clearBoard();
    count("flip");
    const c = center();
    fx.ring(c.x, c.y, palette.accent, ringRadius(1) * 1.2, 0.6);
  };

  const startWheel = () => {
    wheelOn = true;
    wheelHitAt = damage;
    const level = progression.level;
    wheelSpeed = (0.35 + level * 0.07) * difficulty;
    const closing = speedNow + wheelSpeed;
    const gap = sepSeconds() + (2 * Math.max(reach(0), reach(1))) / closing + 0.1;
    const arms = clamp(Math.floor(TAU / (closing * gap)), 3, 8);
    const start = angle + direction * Math.PI;
    for (let k = 0; k < arms; k++) {
      items.push(
        makeItem("spike", "wheel", (k % 2) as Ring, start + (k / arms) * TAU, {
          vel: -direction * wheelSpeed,
          arm: WHEEL_ARM_SECONDS,
        }),
      );
    }
    count("wheel");
    fx.banner("SPIKE WHEEL", "Weave through the spokes", palette.danger);
    bannerShade = 1.8;
    feedbackQueue.push("boss");
  };

  const endWheel = () => {
    wheelOn = false;
    clearBoard();
    wheelsCleared += 1;
    const flawless = damage === wheelHitAt;
    const bonus = Math.round(400 * progression.level * (flawless ? 2 : 1) * multiplier());
    score += bonus;
    const c = center();
    fx.ring(c.x, c.y, palette.accent, ringRadius(1) * 1.4, 0.8);
    fx.burst(c.x, c.y, palette.accent, 30, 260, 0);
    fx.text(c.x, c.y - recordRadius() - 20, `${flawless ? "FLAWLESS " : ""}+${bonus}`, palette.accent, 20);
    feedbackQueue.push("win");
  };

  const hurt = (x: number, y: number) => {
    if (shield) {
      shield = false;
      invulnerable = 1;
      fx.ring(x, y, palette.accent, 90);
      fx.text(x, y - 26, "SHIELD", palette.accent);
      feedbackQueue.push("hurt");
      return;
    }
    lives -= 1;
    damage += 1;
    combo = 0;
    invulnerable = 1.3;
    feedbackQueue.push("hurt");
    fx.kick(12);
    fx.burst(x, y, palette.danger, 26, 240, 0);
    fx.ring(x, y, palette.danger, 90);
    if (lives <= 0) over = true;
  };

  const inspect = (): VinylOrbitDebug => ({
    angle,
    ring,
    effectiveRing: effectiveRing(),
    direction,
    speed: speedNow,
    level: progression.level,
    reach: [reach(0), reach(1)],
    items: items.map((item) => ({ kind: item.kind, pattern: item.pattern, ring: ringAt(item), angle: angleOf(item), age: item.age, dangerous: dangerous(item) })),
    byLevel: JSON.parse(JSON.stringify(byLevel)),
    wheelsCleared,
  });

  return {
    bloom: 0.45,
    inspect,

    resize(nextWidth, nextHeight) {
      width = nextWidth;
      height = nextHeight;
      trail.length = 0;
    },

    autopilot() {
      if (over) return EMPTY_INPUT;
      // Mark, for each ring and each 50ms step ahead, whether a live spike
      // would be touching the player, and how close the near misses are.
      danger.fill(0);
      const margin = 0.035;
      for (let s = 0; s < PLAN_STEPS; s++) {
        const t = (s + 1) * PLAN_STEP;
        if (flipIn >= 0 && t >= flipIn) break;
        const player = angle + direction * speedNow * t;
        for (const item of items) {
          const gap = angleGap(angleOf(item, t), player);
          const r = ringAt(item, t);
          if (item.kind === "spike") {
            if (item.dying >= 0) continue;
            const armed = item.age + t >= item.arm;
            if (armed && gap < reach(r) + margin) danger[s * 2 + r] += 1000;
            else if (gap < reach(r) * 2.6) danger[s * 2 + r] += 3;
          } else if (item.dying < 0 && gap < reach(r) * 1.5) danger[s * 2 + r] -= item.kind === "shield" && !shield ? 30 : 8;
        }
      }
      // Leave a ring early: every step with a hit coming up on it inside
      // 0.6s costs a little, so the bot hops as soon as the other ring is clear.
      for (let r = 0; r < 2; r++) {
        let next = -100;
        for (let s = PLAN_STEPS - 1; s >= 0; s--) {
          if (danger[s * 2 + r] >= 1000) next = s;
          if (next >= 0 && next - s <= 12) danger[s * 2 + r] += 4;
        }
      }
      // Cheapest way through: stay or hop at each step, with a short
      // cooldown after every hop. Filled from the end backwards.
      const stride = (PLAN_COOLDOWN + 1) * 2;
      const at = (s: number, r: number, c: number) => s * stride + c * 2 + r;
      value.fill(0);
      for (let s = PLAN_STEPS - 1; s >= 0; s--) {
        for (let r = 0; r < 2; r++) {
          for (let c = 0; c <= PLAN_COOLDOWN; c++) {
            const here = danger[s * 2 + r];
            let best = value[at(s + 1, r, Math.max(0, c - 1))];
            if (c === 0 && s + 2 < PLAN_STEPS + 3) {
              const other = 1 - r;
              const mid = s + 1 < PLAN_STEPS ? Math.max(danger[(s + 1) * 2 + r], danger[(s + 1) * 2 + other]) : 0;
              const hop = 0.5 + mid + (s + 2 <= PLAN_STEPS ? value[at(s + 2, other, PLAN_COOLDOWN - 2)] : 0);
              if (hop < best) best = hop;
            }
            value[at(s, r, c)] = here + best;
          }
        }
      }
      const r = effectiveRing();
      const ready = switchProgress >= 1 && time - lastTap >= 0.22;
      const stay = value[at(0, r, ready ? 0 : 2)];
      const other = 1 - r;
      const hop = ready ? 0.5 + Math.max(danger[r], danger[other]) + value[at(1, other, PLAN_COOLDOWN - 1)] : Infinity;
      if (hop < stay - 0.01) {
        lastTap = time;
        const p = position(currentRadius(), angle);
        return tapInput(p.x, p.y);
      }
      return EMPTY_INPUT;
    },

    update(dt, input: FrameInput, audio: AudioFrame) {
      if (over) {
        fx.update(dt);
        return;
      }
      time += dt;
      invulnerable = Math.max(0, invulnerable - dt);
      switchProgress = Math.min(1, switchProgress + dt / SWITCH_SECONDS);
      speedNow = speedFor();

      if (pressedAction(input) && switchProgress >= 0.5) {
        ring = ring === 0 ? 1 : 0;
        switchProgress = 0;
        const p = position(currentRadius(), angle);
        fx.burst(p.x, p.y, palette.secondary, 6, 80, 0);
      }

      progression.update(dt, time, combo);
      if (progression.level !== lastLevel) {
        lastLevel = progression.level;
        flipDoneThisLevel = false;
        skipSpikes = 0;
        if (wheelOn) endWheel();
        flip();
        const fresh = unlocked().find((pattern) => !introduced.has(pattern));
        if (fresh) {
          introduced = new Set([...introduced, fresh]);
          pendingNew = fresh;
        }
        feedbackQueue.push("level");
        bannerShade = 1.8;
      }
      if (progression.fever && !wasFever) bannerShade = 1.8;
      wasFever = progression.fever;
      bannerShade = Math.max(0, bannerShade - dt);
      const lt = levelTime();
      if (progression.level >= 3 && !flipDoneThisLevel && flipIn < 0 && lt >= 11 - FLIP_WARN_SECONDS && lt < 12) {
        flipIn = FLIP_WARN_SECONDS;
        flipDoneThisLevel = true;
      }
      if (flipIn >= 0) {
        flipIn -= dt;
        if (flipIn < 0) {
          flip();
          fx.text(width / 2, center().y - recordRadius() - 24, "REVERSE", palette.accent, 20);
        }
      }
      if (!wheelOn && lt >= WHEEL_START && lt < WHEEL_END) startWheel();
      if (wheelOn && lt >= WHEEL_END) endWheel();

      angle += speedNow * dt * direction;
      recordSpin += dt * (1.2 + audio.bass * 2.5);
      wheelSpin -= direction * wheelSpeed * dt;
      score += dt * 8 * difficulty * multiplier();
      if (audio.beat) spawnOnBeat();

      const onRing = effectiveRing();
      const player = position(currentRadius(), angle);
      for (const item of items) {
        item.age += dt;
        item.center += item.vel * dt;
        if (item.dying >= 0) {
          item.dying -= dt;
          if (item.dying <= 0) item.gone = true;
          continue;
        }
        const at = angleOf(item);
        const gap = angleGap(at, angle);
        // Spikes and pickups the player has left well behind fade away.
        const back = direction > 0 ? ahead(at, angle) : ahead(angle, at);
        if (item.pattern !== "wheel" && item.age > 1 && back < 1.2 && back > 0.5) {
          item.dying = 0.3;
          continue;
        }
        const itemRing = ringAt(item);
        if (item.hopAt >= 0 && item.age - dt < item.hopAt && item.age >= item.hopAt) {
          const from = position(ringRadius(item.ring), at);
          fx.burst(from.x, from.y, palette.danger, 6, 90, 0);
        }
        if (itemRing !== onRing) {
          if (dangerous(item) && !item.grazed && gap < reach(itemRing) * 1.6) {
            item.grazed = true;
            combo += 1;
            score += 15 * multiplier();
            if (time - lastNear > 2) {
              lastNear = time;
              fx.text(player.x, player.y - 22, "CLOSE", palette.accent, 14);
              feedbackQueue.push("score");
            }
          }
          continue;
        }
        if (item.kind === "spike") {
          if (!dangerous(item) || gap > reach(itemRing) || invulnerable > 0) continue;
          item.dying = 0.3;
          hurt(player.x, player.y);
          if (over) break;
          continue;
        }
        if (gap > reach(itemRing) * 1.5) continue;
        item.gone = true;
        if (item.kind === "shield") {
          shield = true;
          fx.text(player.x, player.y - 24, "SHIELD", palette.accent, 16);
          fx.ring(player.x, player.y, palette.accent, 60);
        } else {
          combo += 1;
          const gain = Math.round(40 * multiplier());
          score += gain;
          if (combo % 10 === 0) feedbackQueue.push("score");
          fx.burst(player.x, player.y, palette.accent, 12, 150, 0);
          fx.text(player.x, player.y - 24, `+${gain}`, palette.accent);
        }
      }
      items = items.filter((item) => !item.gone);

      trail.unshift(player);
      if (trail.length > 18) trail.pop();
      fx.update(dt);
    },

    draw(ctx, audio) {
      drawCoverBackdrop(ctx, art, palette, width, height, { alpha: 0.4, drift: time * 0.15 });
      const c = center();
      drawLightRays(ctx, c.x, c.y, 12, time * 0.1, palette.primary, 0.018 + audio.pulse * 0.02, Math.hypot(width, height) * 0.6);
      ctx.save();
      fx.applyShake(ctx);
      const recordR = recordRadius() * (1 + audio.pulse * 0.03);

      drawSpectrumRing(ctx, audio, c.x, c.y, recordR * 1.05, recordR * 0.26, recordSpin * 0.1, [palette.primary, palette.secondary]);
      drawVinyl(ctx, art, palette, c.x, c.y, recordR, recordSpin, 0.35);

      const onRing = effectiveRing();
      for (const index of [0, 1] as const) {
        const active = index === onRing;
        ctx.save();
        ctx.strokeStyle = withAlpha(active ? palette.primary : palette.text, active ? 0.14 : 0.05);
        ctx.lineWidth = active ? 12 : 8;
        ctx.beginPath();
        ctx.arc(c.x, c.y, ringRadius(index), 0, TAU);
        ctx.stroke();
        ctx.strokeStyle = withAlpha(active ? palette.primary : palette.text, active ? 0.8 : 0.3);
        ctx.lineWidth = active ? 2 : 1.2;
        ctx.setLineDash(active ? [] : [4, 8]);
        ctx.stroke();
        ctx.restore();
      }

      if (wheelOn) drawWheel(ctx, c);
      if (flipIn >= 0) drawFlipWarning(ctx, c);

      for (const item of items) drawItem(ctx, item, audio);

      ctx.save();
      ctx.globalCompositeOperation = "lighter";
      ctx.lineCap = "round";
      for (let i = 1; i < trail.length; i++) {
        const t = 1 - i / trail.length;
        ctx.strokeStyle = withAlpha(palette.primary, t * 0.55);
        ctx.lineWidth = playerRadius() * 1.6 * t;
        ctx.beginPath();
        ctx.moveTo(trail[i - 1].x, trail[i - 1].y);
        ctx.lineTo(trail[i].x, trail[i].y);
        ctx.stroke();
      }
      ctx.restore();

      const player = position(currentRadius(), angle);
      ctx.save();
      ctx.globalAlpha = invulnerable > 0 && Math.floor(invulnerable * 12) % 2 === 0 ? 0.35 : 1;
      drawGlow(ctx, art, progression.fever ? palette.accent : palette.primary, player.x, player.y, playerRadius() * 3.5, 0.8);
      if (shield) {
        ctx.strokeStyle = withAlpha(palette.accent, 0.8);
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(player.x, player.y, playerRadius() * 2.1, 0, TAU);
        ctx.stroke();
      }
      ctx.fillStyle = "#fff";
      ctx.beginPath();
      ctx.arc(player.x, player.y, playerRadius(), 0, TAU);
      ctx.fill();
      // A small arrow shows which way you are going.
      const heading = angle + direction * Math.PI / 2;
      ctx.fillStyle = palette.primary;
      ctx.beginPath();
      const tip = playerRadius() * 2;
      ctx.moveTo(player.x + Math.cos(heading) * tip, player.y + Math.sin(heading) * tip);
      ctx.lineTo(player.x + Math.cos(heading + 2.5) * playerRadius(), player.y + Math.sin(heading + 2.5) * playerRadius());
      ctx.lineTo(player.x + Math.cos(heading - 2.5) * playerRadius(), player.y + Math.sin(heading - 2.5) * playerRadius());
      ctx.closePath();
      ctx.fill();
      ctx.restore();

      fx.draw(ctx);
      ctx.restore();
      if (bannerShade > 0 && !env.quiet) {
        const alpha = Math.min(1, bannerShade * 2.5, (1.8 - bannerShade) * 8) * 0.6;
        const band = ctx.createLinearGradient(0, height * 0.36, 0, height * 0.6);
        band.addColorStop(0, "rgba(0,0,0,0)");
        band.addColorStop(0.5, `rgba(0,0,0,${alpha})`);
        band.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = band;
        ctx.fillRect(0, height * 0.36, width, height * 0.24);
      }
      fx.drawBanner(ctx, width, height);
      drawBeatFlash(ctx, width, height, progression.fever ? palette.accent : palette.primary, audio.pulse * 0.3 + fx.flash);
    },

    feedback() {
      return feedbackQueue.drain();
    },

    probe(): GameProbe {
      let threats = 0;
      let pickups = 0;
      for (const item of items) {
        if (item.dying >= 0) continue;
        if (item.kind === "spike") threats += 1;
        else pickups += 1;
      }
      const intensity = speedNow * (0.6 + spikeRate());
      return { threats, pickups, spawned: tally.spawned, damage, intensity, boss: wheelOn, mission: null };
    },

    status(): GameStatus {
      return { score: Math.floor(score), lives, maxLives: MAX_LIVES, combo, meter: null, over, level: progression.level, fever: progression.fever };
    },
  };

  function drawItem(ctx: Ctx2D, item: OrbitItem, audio: AudioFrame) {
    const at = angleOf(item);
    const radius = ringRadius(ringAt(item));
    const c = center();
    const fadeIn = Math.min(1, item.age / Math.max(0.2, item.arm));
    const fade = item.dying >= 0 ? Math.max(0, item.dying / 0.35) : 1;
    let p = position(radius, at);
    if (item.pattern === "wheel") {
      // Spokes grow out of the record and only bite once they reach a ring.
      const grow = Math.min(1, item.age / item.arm);
      const reachR = recordRadius() + (radius - recordRadius()) * grow;
      p = position(reachR, at);
      // Spokes are dashed and faint: only the spike at the end can hurt.
      ctx.save();
      ctx.strokeStyle = withAlpha(palette.danger, 0.35 * fade);
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 6]);
      ctx.beginPath();
      const inner = position(recordRadius() * 1.02, at);
      ctx.moveTo(inner.x, inner.y);
      ctx.lineTo(p.x, p.y);
      ctx.stroke();
      ctx.restore();
    }
    ctx.save();
    ctx.globalAlpha = fade;
    if (item.kind === "shield") {
      drawGlow(ctx, art, palette.accent, p.x, p.y, 30, fadeIn);
      ctx.globalAlpha = fade * fadeIn;
      ctx.strokeStyle = "#fff";
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.arc(p.x, p.y, 9, 0, TAU);
      ctx.stroke();
      ctx.fillStyle = palette.accent;
      ctx.font = "800 11px Poppins, sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("S", p.x, p.y + 0.5);
      ctx.restore();
      return;
    }
    if (item.kind === "note") {
      drawGlow(ctx, art, palette.accent, p.x, p.y, 22 + audio.pulse * 6, 0.7 * fadeIn);
      ctx.globalAlpha = fade * fadeIn;
      drawNoteGlyph(ctx, p.x, p.y, 16 + audio.pulse * 3, "#fff");
      ctx.restore();
      return;
    }
    const size = spikeRadius() * 1.6;
    const armed = dangerous(item);
    if (item.pattern === "mover" && item.dying < 0) {
      // The swing range, so you can see where it will be.
      ctx.strokeStyle = withAlpha(palette.danger, 0.25);
      ctx.lineWidth = 4;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.arc(c.x, c.y, radius, item.center - item.amp, item.center + item.amp);
      ctx.stroke();
    }
    if (item.hopAt >= 0 && item.age < item.hopAt && item.dying < 0) {
      // A blinker shows where it is going: a ghost on the other ring and a
      // bar that fills until the jump.
      const ghost = position(ringRadius(item.toRing), at);
      ctx.strokeStyle = withAlpha(palette.danger, 0.5);
      ctx.lineWidth = 2;
      ctx.setLineDash([4, 4]);
      ctx.beginPath();
      ctx.arc(ghost.x, ghost.y, size * 0.9, 0, TAU);
      ctx.moveTo(p.x, p.y);
      ctx.lineTo(ghost.x, ghost.y);
      ctx.stroke();
      ctx.setLineDash([]);
      const fill = Math.min(1, item.age / item.hopAt);
      ctx.fillStyle = withAlpha(palette.danger, 0.6);
      ctx.beginPath();
      ctx.arc(ghost.x, ghost.y, size * 0.9 * fill, 0, TAU);
      ctx.fill();
    }
    if (item.pattern === "comet" && item.dying < 0) {
      // A tail pointing back the way it came.
      const tailDir = item.vel > 0 ? -1 : 1;
      ctx.strokeStyle = withAlpha(palette.danger, 0.4);
      ctx.lineWidth = size * 0.7;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.arc(c.x, c.y, radius, Math.min(at, at + tailDir * 0.35), Math.max(at, at + tailDir * 0.35));
      ctx.stroke();
    }
    if (!armed) {
      // Warning: a hollow outline and a closing ring until it goes live.
      const progress = Math.min(1, item.age / item.arm);
      ctx.strokeStyle = withAlpha(palette.danger, 0.35 + progress * 0.45);
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(p.x, p.y, size * (2.2 - progress * 1.2), 0, TAU);
      ctx.stroke();
      ctx.translate(p.x, p.y);
      ctx.rotate(at);
      traceSpike(ctx, size);
      ctx.setLineDash([3, 3]);
      ctx.stroke();
      ctx.restore();
      return;
    }
    drawGlow(ctx, art, palette.danger, p.x, p.y, size * 2.2, 0.55);
    ctx.translate(p.x, p.y);
    ctx.rotate(at + time * 2);
    traceSpike(ctx, size);
    ctx.fillStyle = palette.danger;
    ctx.fill();
    ctx.strokeStyle = "#fff";
    ctx.lineWidth = 1.5;
    ctx.stroke();
    ctx.fillStyle = "#fff";
    ctx.beginPath();
    ctx.arc(0, 0, size * 0.22, 0, TAU);
    ctx.fill();
    ctx.restore();
  }

  function traceSpike(ctx: Ctx2D, size: number) {
    ctx.beginPath();
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * TAU;
      const r = i % 2 === 0 ? size : size * 0.42;
      if (i === 0) ctx.moveTo(Math.cos(a) * r, Math.sin(a) * r);
      else ctx.lineTo(Math.cos(a) * r, Math.sin(a) * r);
    }
    ctx.closePath();
  }

  function drawWheel(ctx: Ctx2D, c: { x: number; y: number }) {
    ctx.save();
    ctx.strokeStyle = withAlpha(palette.danger, 0.45);
    ctx.lineWidth = 4;
    ctx.setLineDash([14, 10]);
    ctx.lineDashOffset = wheelSpin * 40;
    ctx.beginPath();
    ctx.arc(c.x, c.y, recordRadius() * 1.12, 0, TAU);
    ctx.stroke();
    ctx.restore();
  }

  function drawFlipWarning(ctx: Ctx2D, c: { x: number; y: number }) {
    const blink = Math.floor(flipIn * 6) % 2 === 0 ? 1 : 0.5;
    const r = ringRadius(1) + playerRadius() * 3;
    ctx.save();
    ctx.strokeStyle = withAlpha(palette.accent, 0.8 * blink);
    ctx.lineWidth = 3;
    ctx.lineCap = "round";
    // Chevrons around the outside pointing the new way round.
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * TAU + time * 0.5;
      const back = -direction * 0.08;
      const p0 = position(r - 8, a - back);
      const p1 = position(r, a + back);
      const p2 = position(r + 8, a - back);
      ctx.beginPath();
      ctx.moveTo(p0.x, p0.y);
      ctx.lineTo(p1.x, p1.y);
      ctx.lineTo(p2.x, p2.y);
      ctx.stroke();
    }
    ctx.restore();
    glowText(ctx, "REVERSE", c.x, c.y - recordRadius() - 26, Math.max(14, base() * 0.035), palette.accent, blink);
  }

  function drawNoteGlyph(ctx: Ctx2D, x: number, y: number, size: number, color: string) {
    ctx.save();
    ctx.fillStyle = color;
    ctx.strokeStyle = color;
    ctx.lineWidth = Math.max(1.5, size * 0.14);
    ctx.beginPath();
    ctx.ellipse(x - size * 0.2, y + size * 0.3, size * 0.32, size * 0.24, -0.4, 0, TAU);
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(x + size * 0.1, y + size * 0.28);
    ctx.lineTo(x + size * 0.1, y - size * 0.5);
    ctx.quadraticCurveTo(x + size * 0.5, y - size * 0.3, x + size * 0.42, y - size * 0.02);
    ctx.stroke();
    ctx.restore();
  }
}
